import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Download, Loader2, Send } from 'lucide-react';
import { askInsights, type AskResult } from '@/lib/admin/insights-api';
import { toast } from '@/hooks/use-toast';
import { AskResultView } from './AskResultView';
import { downloadAskPdf } from './askPdf';

const DEFAULT_PROMPT =
  'Weekly leadership brief: what changed across pulse and anchors in the last 30 days, where risk is concentrated by segment, and what leaders should focus on next week.';

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  surveyId?: string;
  initialQuestion?: string;
  title?: string;
};

export function AskBriefDialog({
  open,
  onOpenChange,
  orgId,
  surveyId,
  initialQuestion,
  title = 'Download Ask brief',
}: Props) {
  const [question, setQuestion] = useState(initialQuestion ?? DEFAULT_PROMPT);
  const [asked, setAsked] = useState('');
  const [result, setResult] = useState<AskResult | null>(null);
  const [downloading, setDownloading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQuestion(initialQuestion ?? DEFAULT_PROMPT);
      setResult(null);
      setAsked('');
    }
  }, [open, initialQuestion]);

  const mut = useMutation({
    mutationFn: (q: string) => askInsights({ orgId, surveyId, question: q }),
    onSuccess: (d) => setResult(d),
    onError: (e: Error) =>
      toast({ title: 'Ask failed', description: e.message, variant: 'destructive' }),
  });

  const runAsk = () => {
    const t = question.trim();
    if (t.length < 3) return;
    setAsked(t);
    mut.mutate(t);
  };

  const runDownload = async () => {
    if (!ref.current || !result) return;
    setDownloading(true);
    try {
      await downloadAskPdf({
        element: ref.current,
        filenameSlug: `ask-brief-${asked}`,
        headerTitle: 'CultureLens — Ask brief',
      });
    } catch (e) {
      toast({
        title: 'Could not generate PDF',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Generate a privacy-safe leadership brief. Answers use only k≥5 aggregated signals.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={4}
            maxLength={500}
            disabled={mut.isPending}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{question.length}/500</span>
            <Button size="sm" onClick={runAsk} disabled={mut.isPending || question.trim().length < 3}>
              {mut.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {result ? 'Re-run' : 'Generate'}
            </Button>
          </div>

          {result && <AskResultView ref={ref} question={asked} result={result} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={runDownload} disabled={!result || downloading}>
            {downloading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
